import cn from "app/utils/classnames";
import { useCallback, useEffect, useState } from "react";

interface KeyboardButtonProps {
  text: string;
  onPress: (keyText: string) => void;
  disabled?: boolean;
  className?: string;

  // The button will play its press animation when the corresponding key on
  // the device's physical keyboard is used.
  physKeycode?: string;

  // If true, onPress will be called when physical keypresses are detected.
  // By default, only clicks will be recognized.
  callOnPressForPhysKeys?: boolean;
}

export default function KeyboardButton({
  text,
  onPress,
  disabled,
  className,
  physKeycode,
  callOnPressForPhysKeys,
}: KeyboardButtonProps) {
  const [physKeyDown, setPhysKeyDown] = useState(false);

  // Make the button press/depress based on its matching physical keyboard
  // button.
  const keyFilter = useCallback(
    (e: KeyboardEvent) => {
      if (disabled) {
        return false;
      }
      const comp = physKeycode ?? text.toLowerCase();
      return e.key.toLowerCase() === comp.toLowerCase();
    },
    [physKeycode, text, disabled],
  );

  // Handle physical keypress down.
  useEffect(() => {
    function onPhysKeyDown(e: KeyboardEvent) {
      if (!keyFilter(e)) {
        return;
      }
      setPhysKeyDown(true);
      if (callOnPressForPhysKeys) {
        onPress?.(text);
      }
    }

    window.addEventListener("keydown", onPhysKeyDown);
    return () => {
      window.removeEventListener("keydown", onPhysKeyDown);
    };
  }, [callOnPressForPhysKeys, onPress, text, keyFilter]);

  // Handle phys keypress up
  useEffect(() => {
    function onPhysKeyUp() {
      setPhysKeyDown(false);
    }
    window.addEventListener("keyup", onPhysKeyUp);
    return () => {
      window.removeEventListener("keyup", onPhysKeyUp);
    };
  }, []);

  return (
    <button
      onClick={() => {
        if (!disabled) {
          onPress(text);
        }
      }}
      onContextMenu={(e) => e.preventDefault()}
      tabIndex={-1}
      className={cn(
        "p-1.5 min-w-1 lg:min-w-10 h-full lg:h-14",
        "shrink basis-[9.25%]",
        "bg-white border border-black rounded-sm",
        "shadow-[1px_1px_0_0px_black] -translate-px",
        "lg:shadow-[4px_4px_0_0px_black] lg:-translate-1",
        "active:shadow-[0px_0px_0_0px_black] active:translate-0",
        physKeyDown &&
          "shadow-[0px_0px_0_0px_black] translate-0 lg:shadow-[0px_0px_0_0px_black] lg:translate-0",
        "transition-all duration-10",
        "flex justify-center items-center",
        "text-black uppercase font-mono select-none [webkit-touch-callout:none]",
        className,
      )}
    >
      <p>{text}</p>
    </button>
  );
}
